import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from "react-router-dom";
import { Button, IconButton, Typography, Grid, Box, Stack, MenuItem, FormHelperText, TextField, InputAdornment, Select } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { visuallyHidden } from '@mui/utils'
import { useConfirm } from '@components/ConfirmProvider'
import OrgSelectModal from '@components/Modal/OrgSelectModal'
import { sendEmailCode, verifyEmailCode, getEmailCheck } from "@api/member";
import AuthTimer from './AuthTimer'
import { useIsMobile } from '@/hooks/Responsive'

// ----------------------- 검증 유틸 ------------------------
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&+=]).{10,}$/;

export default function Step4({ MemberType, type, MemberKind, kind, verified, onSubmit, reset, tempMemberInfo }) {
  const { isMobile } = useIsMobile()
  const navigate = useNavigate();
  const { alert, confirm } = useConfirm();

  // 핸드폰 자리수 분리
  const splitPhone = (num, part) => {
    const s = String(num ?? "").replace(/\D/g, "");
    // 앞자리 2~3자리(02 같은 유선도 허용), 중간 3~4자리, 뒤 4자리
    const m = s.match(/^(\d{2,3})(\d{3,4})(\d{4})$/);
    if (!m) return part ? "" : { phone1: "", phone2: "", phone3: "" };

    const result = { phone1: m[1], phone2: m[2], phone3: m[3] };
    if (!part) return result;
    if (part === 1) return result.phone1;
    if (part === 2) return result.phone2;
    if (part === 3) return result.phone3;
    return ""; // 잘못된 part 값
  };

  // 항목데이터
  const [form, setForm] = useState({
    name: verified?.name || '',  // 이름
    gender: verified?.gender || '',  // 성별
    birthYear: verified?.birthDay || '',  // 생년월일
    phone1: splitPhone(verified?.phone, 1) || '',  // 휴대전화번호1
    phone2: splitPhone(verified?.phone, 2) || '',  // 휴대전화번호2
    phone3: splitPhone(verified?.phone, 3) || '',  // 휴대전화번호3
    emailLocal: '',  // 이메일아이디
    emailDomain: '',  // 이메일 주소
    emailPreset: 'custom',  // 이메일직접입력
    emailCode: '',  // 이메일 인증코드
    password: '',  // 패스워드
    passwordConfirm: '',  // 패스워드 확인
    nationality: verified?.nationality || (kind === MemberKind.FRNR ? "FOREIGNER" : "LOCAL"),  // 국적구분
    content: '',  // 관심XR콘텐츠
    course: '',  // 관심강좌
    // (기관학습자)
    organizationCode: '',  // 기관코드
    organizationName: '',  // 기관명
    organizationEmail: '',  // 기관 이메일아이디
    organizationEmailDomain: '',  // 기관 이메일 주소
    organizationDetail: '',  // 기관상세
  });

  const [emailVerified, setEmailVerified] = useState(false);  // 이메일 인증여부
  const [emailCertToken, setEmailCertToken] = useState('');  // 이메일 인증 성공 시 서버 발급 토큰
  const [password, setPassword] = useState(false)  // 비밀번호 토글
  const [passwordConfirm, setPasswordConfirm] = useState(false)  // 비밀번호확인 토글
  const [errors, setErrors] = useState({})  // 오류저장
  


  useEffect(() => {
    // csv파일로 등록된 회원인 경우 등록된 데이터로 입력
    if (tempMemberInfo) {
    const [id, domain] = tempMemberInfo?.email?.split("@");
    setForm((p) => ({
      ...p,
      emailLocal: id,
      emailDomain: domain,
      organizationCode: tempMemberInfo?.instCode,
      organizationName: tempMemberInfo?.instName,
    }));
    setEmailVerified(true);  // 임시회원인 경우 등록을 위해 처리
    }
  }, []);

  // phone컬럼은 숫자만 입력되게
  const onlyDigits = (str = '') => str.replace(/[^0-9]/g, '')
  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((p) => ({
      ...p,
      [name]: name.includes('phone') || name.includes('emailCode') ? onlyDigits(value) : value,
    }))
  }

  // 이메일 직접입력 선택시
  const handleEmailPreset = (e) => {
    const v = e.target.value
    setForm((p) => ({
      ...p,
      emailPreset: v,
      emailDomain: v === 'custom' ? '' : v
    }))
  }
  
  const handleClickShowPassword = () => {
    setPassword((prev) => !prev)
  }
  const handleClickShowPasswordConfirm = () => {
    setPasswordConfirm((prev) => !prev)
  }

  // 유효성 검사
  const validate = () => {
    let tempErrors = {
      name: '', 
      gender: '', 
      birthDay: '', 
      phone1: '', 
      phone2: '', 
      phone3: '', 
      phone: '', 
      emailLocal: '', 
      emailDomain: '', 
      email: '', 
      emailCode: '', 
      password: '', 
      passwordConfirm: '' 
    }

    const email = `${form.emailLocal}@${form.emailDomain}`;

    if (!form.name) tempErrors.name = "이름을 입력해 주세요.";
    if (!form.gender) tempErrors.gender = "성별을 선택해 주세요.";
    if (!form.birthYear) tempErrors.birthYear = "출생연도를 선택해 주세요.";
    if (!form.phone1) { tempErrors.phone1 = "휴대전화번호를 입력해 주세요."; tempErrors.phone = "휴대전화번호를 입력해 주세요."; }
    if (!form.phone2) { tempErrors.phone2 = "휴대전화번호를 입력해 주세요."; tempErrors.phone = "휴대전화번호를 입력해 주세요."; }
    if (!form.phone3) { tempErrors.phone3 = "휴대전화번호를 입력해 주세요."; tempErrors.phone = "휴대전화번호를 입력해 주세요."; }
    if (type === MemberType.INST && !form.organizationCode) { tempErrors.organizationCode = "기관정보를 선택해 주세요."; tempErrors.organizationName = "기관정보를 선택해 주세요."; }
    if (type === MemberType.INST && !form.organizationDetail) { tempErrors.organizationDetail = "기관상세정보를 선택해 주세요."; }
    if (!form.emailLocal) tempErrors.emailLocal = "이메일을 입력해 주세요.";
    if (!form.emailDomain) tempErrors.emailDomain = "이메일을 입력해 주세요.";
    if (!emailRegex.test(email)) tempErrors.email = "이메일 형식이 올바르지 않습니다.";
    if (!emailVerified) tempErrors.emailCode = "이메일 인증을 완료해 주세요.";
    if (!form.password) tempErrors.password = "비밀번호를 입력해 주세요.";
    if (!passwordRegex.test(form.password)) tempErrors.password = "비밀번호 규정을 확인해 주세요.";
    if (!form.passwordConfirm) tempErrors.passwordConfirm = "비밀번호 확인을 입력해 주세요.";
    if (!passwordRegex.test(form.passwordConfirm)) tempErrors.passwordConfirm = "비밀번호 규정을 확인해 주세요.";
    if (form.password !== form.passwordConfirm) tempErrors.passwordConfirm = "비밀번호가 일치하지 않습니다.";

    setErrors(tempErrors)
    return Object.values(tempErrors).every(x => x === '')
  }

  // 기관검색 창
  const [dialogOpen, setDialogOpen] = useState(false)
  const handleOpenDialog = () => setDialogOpen(true)
  const handleCloseDialog = () => setDialogOpen(false)

  // 기관검색 다이얼로그에서 콜백
  const handleConfirmInstitution = (inst) => {
    setForm((p) => ({ ...p,
      organizationCode: inst.insttId,
      organizationName: inst.insttNm
    }));
  };

  // 출생연도 표시
  const currentYear = new Date().getFullYear();
  const yearRange = { start: currentYear - 100, end: currentYear };

  const years = useMemo(() => {
    const arr = [];
    for (let y = yearRange.start; y <= yearRange.end; y++) {
      arr.push(String(y)); // Select value와 타입을 맞추기 위해 문자열화
    }
    return arr;
  }, [yearRange.start, yearRange.end]);

  // 이메일 체크
  const email = `${form.emailLocal}@${form.emailDomain}`
  const isEmailValid = emailRegex.test(email);

  // 이메일 등록여부
  const checkEmail = async () => {
    const email = `${form.emailLocal}@${form.emailDomain}`;

    // validation
    if (!emailRegex.test(email)) return alert("이메일 형식이 올바르지 않습니다.");

    try {
      const params = {
        memberEmail : email,
      };
      const response = await getEmailCheck(params);

      if (response?.success) {
        // 기존에 등록된 회원이 없는 경우
        if (response?.data?.registedMemberYn == 'N') {
          sendCode(email);  // 이메일인증 발송
        }
        // 기존에 등록된 회원이 있는 경우
        else {
          alert('이미 등록된 사용자입니다.\n아이디/비밀번호 찾기를 해 주세요.');
        }
      }
      else {
        alert(response?.message || "이메일 체크 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 이메일발송 만료체크
  const [timerKey, setTimerKey] = useState(0);  // 타이머 리셋용
  const [expired, setExpired] = useState(false);  // 만료 여부 체크
  const [codeSent, setCodeSent] = useState(false); // 코드가 한번이라도 발송됐는지
  const [emailInputDisabled, setEmailInputDisabled] = useState(false); // 이메일 관련 입력 잠금 여부
  
  // 이메일인증 발송
  const sendCode = async (email) => {
    try {
      // 초기화
      setForm((p) => ({ ...p, emailCode: '' }));  // 인증코드
      setEmailVerified(false);  // 인증여부
      setEmailCertToken('');

      const response =  await sendEmailCode(email, form.name);

      if (response?.resultCode === "00") {
        alert(response?.resultMsg);

        // 🔹 이메일 입력 관련 잠금
        setEmailInputDisabled(true);

        // key 값을 변경해서 AuthTimer를 "다시 마운트" 시킴 = 시간 리셋
        setTimerKey(prev => prev + 1);
        setExpired(false);  // 타이머 다시 시작하니까 만료 상태 해제
        setCodeSent(true);  // 코드 한번이라도 발송됨
      }
      else {
        alert(response?.resultMsg);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 이메일인증 검증
  const checkCode = async () => {
    const email = `${form.emailLocal}@${form.emailDomain}`;

    if (!form.emailCode) {
      alert('인증코드를 입력해 주세요.');
      return;
    }

    try {
      const response = await verifyEmailCode(email, form.emailCode);
 
      if (response?.resultCode === "00") {
        if (!response?.certToken) {
          alert('본인인증 정보를 확인하지 못했습니다. 다시 시도해 주세요.');
          return;
        }
        // 🔹 이메일 인증 완료 처리
        setEmailCertToken(response.certToken);
        setEmailVerified(true);
        setEmailInputDisabled(true);  // 혹시 몰라서 다시 잠금 (중복 안전장치)
        
        // 타이머 초기화
        setExpired(false);  // 만료 초기화
        setCodeSent(false);  // 발송버튼 초기화

        alert(response?.resultMsg);
      }
      // 이메일 재발송(인증 횟수를 초과 or 메일발송이 안되었거나 인증이 만료)
      else if (response?.resultCode === "99") {
        setEmailInputDisabled(false);  // 이메일 관련 입력 잠금 해제

        // 타이머 초기화
        setExpired(false);  // 만료 초기화
        setCodeSent(false);  // 발송버튼 초기화
        alert(response?.resultMsg);
      }
      else {
        alert(response?.resultMsg);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // // 이메일인증 검증 pass(테스트용)
  // const checkCodeTemp = async () => {
  //   setForm((p) => ({ ...p, emailCode: '123456' }));
  //   setEmailVerified(true);

  //   // 타이머 초기화
  //   setExpired(false);  // 만료 초기화
  //   setCodeSent(false);  // 발송버튼 초기화
  // };

  // 회원정보 등록
  const submit = async () => {
    if (!validate()) return;
    
    const memberInfo = { 
      type: type,
      kind: kind,
      name: form.name, 
      password: form.password, 
      email: `${form.emailLocal}@${form.emailDomain}`, 
      phone: `${form.phone1}${form.phone2}${form.phone3}`, 
      gender: form.gender, 
      birthDay: form.birthYear, 
      nationality: form.nationality, 
      instCode: form.organizationCode,
      instName: form.organizationName,
      instClass: type === MemberType.GNRL ? 'GENERAL' : form.organizationDetail,  // 일반회원인경우 GENERAL로 등록
      content: form.content,
      course: form.course,
      certToken: emailCertToken || verified?.certToken || '',
    };
    
    await onSubmit(memberInfo);
  };

  // 항목 view데이터(disabled)
  const SOCIAL_KINDS = new Set(['kakao', 'naver', 'google']);
  const viewFlag = (kind) => !SOCIAL_KINDS.has(kind);  // sns가입이 아닌 경우
  const tempMemberFlag = tempMemberInfo ? true : false;  // 임시회원 유무
  const viewForm = useMemo(() => ({
    name: viewFlag(kind),  // 이름
    gender: viewFlag(kind),  // 성별
    birthYear: viewFlag(kind),  // 생년월일
    nationality: false,  // 국적
    phone1: true,  // 휴대전화번호1
    phone2: true,  // 휴대전화번호2
    phone3: true,  // 휴대전화번호3
    organizationCode: tempMemberFlag,  // 기관코드
    organizationName: tempMemberFlag,  // 기관명
    organizationDetail: false,  // 기관상세
    emailLocal: tempMemberFlag,  // 이메일아이디
    emailDomain: tempMemberFlag,  // 이메일 주소
    emailCode: false,  // 이메일 인증코드
  }), [kind, tempMemberFlag]);



  return (
    <Box>
      <Typography component="h3" variant={isMobile ? 'h6' : 'h5'} sx={{ mb: isMobile ? 4 : 6 }}>
        회원정보입력
      </Typography>

      <Box>
        {/* 이름 성별 */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography fontWeight={500} sx={{ mb: 1 }}>
              이름 <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <TextField 
              fullWidth 
              id="name" 
              name="name" 
              placeholder="이름을 입력해 주세요" 
              inputProps={{ maxLength: 30 }}
              value={form.name} 
              sx={{ flex: 1 }} 
              onChange={handleChange} 
              error={!!errors.name} 
              disabled={viewForm.name}
            />
            {errors.name && (<FormHelperText error>{errors.name}</FormHelperText>)}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography fontWeight={500} sx={{ mb: 1 }}>
              성별 <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <Select
              id="gender"
              name="gender"
              value={form.gender}
              onChange={handleChange}
              error={!!errors.gender}
              displayEmpty
              disabled={viewForm.gender}
              fullWidth
            >
              {/* placeholder */}
              <MenuItem value="">
                성별을 선택해 주세요
              </MenuItem>
              <MenuItem value="M">남자</MenuItem>
              <MenuItem value="F">여자</MenuItem>
            </Select>
            {errors.gender && (<FormHelperText error>{errors.gender}</FormHelperText>)}
          </Grid>
        </Grid>
        
        {/* 출생연도 국적 */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography fontWeight={500} sx={{ mb: 1 }}>
              출생연도 <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <Select
              id="birthYear"
              name="birthYear"
              value={form.birthYear}
              onChange={handleChange}
              error={!!errors.birthYear}
              displayEmpty
              disabled={viewForm.birthYear}
              fullWidth
            >
              {/* placeholder */}
              <MenuItem value="">
                출생연도를 선택해 주세요
              </MenuItem>

              {/* 동적 생성된 연도 옵션 */}
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
            {errors.birthYear && (<FormHelperText error>{errors.birthYear}</FormHelperText>)}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography fontWeight={500} sx={{ mb: 1 }}>
              국적
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <Select
              id="nationality"
              name="nationality"
              value={form.nationality}
              onChange={handleChange}
              error={!!errors.nationality}
              displayEmpty
              disabled={viewForm.nationality}
              fullWidth
            >
              {/* placeholder */}
              <MenuItem value="">
                국적을 선택해 주세요
              </MenuItem>
              <MenuItem value="LOCAL">내국인</MenuItem>
              <MenuItem value="FOREIGNER">외국인</MenuItem>
            </Select>
          </Grid>
        </Grid>

        {/* 휴대전화번호 */}
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={500} sx={{ mb: 1 }}>
            휴대전화번호 <Typography component="span" color="error">*</Typography>
            <Box component="span" sx={visuallyHidden}>필수 입력</Box>
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <TextField
              id="phone1"
              name="phone1"
              type="tel"
              placeholder="번호를 입력해 주세요"
              value={form.phone1}
              onChange={handleChange}
              error={!!errors.phone1}
              inputProps={{ inputMode: 'numeric', maxLength: 3 }}
              disabled={viewForm.phone1}
              fullWidth
            />
            <Typography component="span">-</Typography>

            <TextField
              name="phone2"
              type="tel"
              placeholder="번호를 입력해 주세요"
              value={form.phone2}
              onChange={handleChange}
              error={!!errors.phone2}
              inputProps={{ inputMode: 'numeric', maxLength: 4 }}
              disabled={viewForm.phone2}
              fullWidth
            />
            <Typography component="span">-</Typography>

            <TextField
              name="phone3"
              type="tel"
              placeholder="번호를 입력해 주세요"
              value={form.phone3}
              onChange={handleChange}
              error={!!errors.phone3}
              inputProps={{ inputMode: 'numeric', maxLength: 4 }}
              disabled={viewForm.phone3}
              fullWidth
            />
          </Stack>
          {errors.phone && (<FormHelperText error>{errors.phone}</FormHelperText>)}
        </Box>

        {/* 이메일 주소 */}
        {/* 임시회원이 있는 경우 */}
        {tempMemberFlag ? (
          <Box sx={{ mb: 4 }}>
            <Typography fontWeight={500} sx={{ mb: 1 }}>
              아이디(이메일 주소) <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField 
                id="emailLocal" 
                name="emailLocal" 
                placeholder="이메일 아이디를 입력해 주세요" 
                inputProps={{ maxLength: 20 }}
                value={form.emailLocal} 
                onChange={handleChange} 
                disabled={viewForm.emailLocal}
                sx={{ flex: 1 }} 
              />
              <Typography component="span">@</Typography>
              <TextField 
                id="emailDomain" 
                name="emailDomain" 
                placeholder="이메일 주소를 입력해 주세요"
                inputProps={{ maxLength: 20 }}
                value={form.emailDomain} 
                onChange={handleChange} 
                disabled={viewForm.emailDomain}
                sx={{ flex: 1 }} 
              />
            </Stack>
          </Box>
        ) : (
          <Box sx={{ mb: 4 }}>
            <Typography fontWeight={500} sx={{ mb: 1 }}>
              아이디(이메일 주소) <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField 
                  id="emailLocal" 
                  name="emailLocal" 
                  placeholder="이메일 아이디를 입력해 주세요" 
                  inputProps={{ maxLength: 20 }}
                  value={form.emailLocal} 
                  onChange={handleChange} 
                  error={!!errors.emailLocal} 
                  disabled={viewForm.emailLocal || emailInputDisabled || emailVerified}  // 🔹 변경
                />
                <Typography component="span">@</Typography>
                <TextField
                  name="emailDomain"
                  placeholder="이메일 주소를 입력해 주세요"
                  inputProps={{ maxLength: 20 }}
                  value={form.emailDomain}
                  onChange={handleChange}
                  error={!!errors.emailDomain}
                  disabled={emailInputDisabled || emailVerified || form.emailPreset !== 'custom'}  // 🔹 변경
                />
              </Stack>
              <Select
                name="emailPreset"
                value={form.emailPreset}
                onChange={handleEmailPreset}
                sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}
                disabled={emailInputDisabled || emailVerified}  // 🔹 추가
              >
                <MenuItem value="custom">직접입력</MenuItem>
                <MenuItem value="naver.com">naver.com</MenuItem>
                <MenuItem value="hanmail.net">hanmail.net</MenuItem>
                <MenuItem value="kakao.com">kakao.com</MenuItem>
                <MenuItem value="nate.com">nate.com</MenuItem>
                <MenuItem value="gmail.com">gmail.com</MenuItem>
                <MenuItem value="hotmail.com">hotmail.com</MenuItem>
              </Select>
            </Stack>
            {errors.email && (<FormHelperText error>{errors.email}</FormHelperText>)}
            <Button
              size="medium"
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => {checkEmail();}}
              disabled={
                !isEmailValid ||
                (codeSent && !expired) ||
                emailVerified               // 🔹 인증완료 시 완전히 막기
              }
               sx={{ mt: 1.5 }}
            >
              {codeSent
                ? (expired ? '인증코드 재발송' : '인증코드 확인중...')
                : '인증코드 발송'}
            </Button>
            
            <Stack direction="row" spacing={1} alignItems="center"sx={{ mt: 3 }}>
              <TextField
                id="emailCode"
                name="emailCode"
                type="text"
                placeholder="인증코드를 입력해 주세요"
                inputProps={{ maxLength: 6 }}
                value={form.emailCode}
                onChange={handleChange}
                error={!!errors.emailCode}
                disabled={!codeSent || emailVerified}   // 🔹 인증 완료 시 입력 막기
                sx={{ flex: 1 }}
                InputProps={{
                  endAdornment: (codeSent && !emailVerified) && (
                    <Box sx={{ mr: 2 }}>
                      <AuthTimer
                        key={timerKey}
                        initialSeconds={300}
                        onExpire={() => setExpired(true)}
                      />
                    </Box>
                  ),
                }}
              />
              <Button
                size="medium"
                variant="outlined"
                color="primary"
                disabled={!codeSent || emailVerified}   // 🔹 인증 완료 시 확인 버튼도 막기
                onClick={() => {checkCode();}}
              >
                확인
              </Button>
              {/* <Button
                size="medium"
                variant="outlined"
                color="primary"
                onClick={() => {checkCodeTemp();}}
              >
                패스
              </Button> */}
            </Stack>
            {errors.emailCode && (<FormHelperText error>{errors.emailCode}</FormHelperText>)}
            <FormHelperText
              sx={{
                mt: 1.5,
                position: 'relative',
                pl: '1em',
                typography: 'body2',
                color: 'text.primary',
                '&::before': {
                  content: '"•"',
                  position: 'absolute',
                  left: 0,
                },
              }}
            >
              입력하신 이메일로 발송된 인증코드를 입력해 주세요. 메일을 받지 못하신 경우, 스팸함을 확인해 주세요.
            </FormHelperText>
          </Box>
        )}
        
        {/* 비밀번호 */}
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={500} sx={{ mb: 1 }}>
            비밀번호 <Typography component="span" color="error">*</Typography>
            <Box component="span" sx={visuallyHidden}>필수 입력</Box>
          </Typography>
          <TextField
            fullWidth
            id="password"
            name="password"
            type={password ? 'text' : 'password'}
            placeholder="비밀번호를 입력해 주세요"
            value={form.password}
            onChange={handleChange}
            error={!!errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 3 }}>
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {password ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 20 }}
          />
          {errors.password && (<FormHelperText error>{errors.password}</FormHelperText>)}
          <FormHelperText>
            대소문자구분 영문, 숫자, 특수문자를 조합하여 10자 이상 입력. (! @ # $ % ^ & + = 사용가능)
          </FormHelperText>
        </Box>
        
        {/* 비밀번호 확인 */}
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={500} sx={{ mb: 1 }}>
            비밀번호 확인 <Typography component="span" color="error">*</Typography>
            <Box component="span" sx={visuallyHidden}>필수 입력</Box>
          </Typography>
          <TextField
            fullWidth
            id="passwordConfirm"
            name="passwordConfirm"
            type={passwordConfirm ? 'text' : 'password'}
            placeholder="비밀번호를 입력해 주세요"
            value={form.passwordConfirm}
            onChange={handleChange}
            error={!!errors.passwordConfirm}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 3 }}>
                  <IconButton
                    onClick={handleClickShowPasswordConfirm}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {passwordConfirm ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 20 }}
          />
          {errors.passwordConfirm && (<FormHelperText error>{errors.passwordConfirm}</FormHelperText>)}
          <FormHelperText>비밀번호를 한번 더 입력 해 주세요.</FormHelperText>
        </Box>
      </Box>

      {/* (기관학습자)만 */}
      {type === MemberType.INST && (
        <>
          <Typography component="h4" variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ mt: 6, mb: 4 }}>기관정보 입력</Typography>

          {/* 기관정보 */}
          <Box sx={{ mb: 4 }}>
            <Typography fontWeight={500} sx={{ mb: 1 }}>
              기관명 <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems="center" sx={{ flex: 1 }}>
              {/* <TextField 
                fullWidth 
                id="organizationCode" 
                name="organizationCode" 
                placeholder="기관코드" 
                value={form.organizationCode} 
                sx={{ flex: 1 }} 
                onChange={handleChange} 
                error={!!errors.organizationCode} 
                disabled={viewForm.organizationCode}
              /> */}
              <TextField 
                fullWidth 
                id="organizationName" 
                name="organizationName" 
                placeholder="기관검색을 통해 입력해 주세요" 
                value={form.organizationName} 
                onChange={handleChange} 
                error={!!errors.organizationName} 
              //  disabled={viewForm.organizationName}
                disabled
                readonly
                sx={{ flex: 1 }}
              />
            {/* 임시회원이 있는 경우 */}
            {tempMemberFlag ? (
              <></>
              ) : (
              <Button
                size="medium"
                variant="outlined"
                color="primary"
                onClick={handleOpenDialog}
                disabled={viewForm.organizationCode}
              >
                기관검색
              </Button>
            )}
            </Stack>
            {errors.organizationCode && (<FormHelperText error sx={{ mt: 0.5 }}>{errors.organizationCode}</FormHelperText>)}
          </Box>

          {/* 기관상세정보 */}
          <Box sx={{ mb: 4 }}>
            <Typography fontWeight={500} sx={{ mb: 1 }}>
              기관상세정보 <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <Select
              id="organizationDetail"
              name="organizationDetail"
              value={form.organizationDetail}
              onChange={handleChange}
              error={!!errors.organizationDetail}
              displayEmpty
              disabled={viewForm.organizationDetail}
              fullWidth
            >
              {/* placeholder */}
              <MenuItem value="">
                기관상세정보를 선택해 주세요
              </MenuItem>
              <MenuItem value="STUDENT">학생</MenuItem>
              <MenuItem value="ASSISTANT">조교</MenuItem>
              <MenuItem value="PROFESSOR">교수자</MenuItem>
              <MenuItem value="CP">CP</MenuItem>
              <MenuItem value="ORGAN">기관</MenuItem>
            </Select>
            {errors.organizationDetail && (<FormHelperText error sx={{ mt: 0.5 }}>{errors.organizationDetail}</FormHelperText>)}
            <FormHelperText
              sx={{
                mt: 1.5,
                position: 'relative',
                pl: '1em',
                typography: 'body2',
                color: 'text.primary',
                '&::before': {
                  content: '"•"',
                  position: 'absolute',
                  left: 0,
                },
              }}
            >
              CP, 기관 관계자 및 관련 회원 가입 시 인증을 위해 추가 서류를 요청을 할 수 있습니다.
            </FormHelperText>
          </Box>
        </>
      )}

      {/* 관심XR콘텐츠 / 강좌 */}
      {/* <FormRow label="관심XR콘텐츠 / 강좌" htmlFor="content">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <FormControl fullWidth sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
            <Select
              name="content"
              value={form.content}
              onChange={handleChange}
              displayEmpty
              sx={{ flex: 1, backgroundColor: (theme) => theme.palette.background.paper }}
            >
              <MenuItem disabled value="" sx={{ display: 'none' }}>관심XR콘텐츠</MenuItem>
              <MenuItem value="관심XR콘텐츠1">관심XR콘텐츠1</MenuItem>
              <MenuItem value="관심XR콘텐츠2">관심XR콘텐츠2</MenuItem>
              <MenuItem value="관심XR콘텐츠3">관심XR콘텐츠3</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>  
            <Select
              name="course"
              value={form.course}
              onChange={handleChange}
              displayEmpty
              sx={{ flex: 1, backgroundColor: (theme) => theme.palette.background.paper }}
            >
              <MenuItem disabled value="" sx={{ display: 'none' }}>관심강좌</MenuItem>
              <MenuItem value="관심강좌1">관심강좌1</MenuItem>
              <MenuItem value="관심강좌2">관심강좌2</MenuItem>
              <MenuItem value="관심강좌3">관심강좌3</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </FormRow> */}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 5 }}>
        {/* <Button 
          size="large"
          variant="outlined"
          onClick={
            async () => {
              const result = await confirm({ title: '안내', message: '정말로 취소하시겠습니까?' })
              if (result) {
                // 초기화
                reset();
              } else {
                console.log('취소')
              }
            }
          }
          >
          회원등록 취소
        </Button> */}
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={() => { submit(); }}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          회원가입
        </Button>
      </Stack>

      <OrgSelectModal open={dialogOpen} onClose={handleCloseDialog} onSelect={handleConfirmInstitution} />
    </Box>
  )
}